import { useMemo } from "react";
import { ProcessedCourse } from "@/common/course/types";
import { CommercePath, useBuildCommerceUrl } from "@/hooks/useBuildCommerceUrl";

/**
 * Custom hook to generate a checkout URL for a course using Commerce Coordinator.
 * Extracts the appropriate SKU (verified or professional) from the course's active run
 * and builds a commerce URL with proper parameters.
 *
 * @param course - The processed course object
 * @returns The commerce checkout URL with SKU and course run key parameters
 *
 * @example
 * const checkoutUrl = useCourseCheckoutUrl(course);
 * // Returns: "/lms/payment_page_redirect/?sku=ABC123&course_run_key=course-v1:..."
 */
export function useCourseCheckoutUrl(course: ProcessedCourse): string {
  // Extract SKU for commerce URL (verified or professional seat)
  const courseRunSku = useMemo(() => {
    if (!course.activeCourseRun?.seats) return undefined;
    const skus = [
      course.activeCourseRun.seats.find((seat) => seat.type === "verified")
        ?.sku,
      course.activeCourseRun.seats.find((seat) => seat.type === "professional")
        ?.sku,
    ];
    return skus[0] ?? skus[1];
  }, [course.activeCourseRun?.seats]);

  // Build commerce checkout URL using the hook
  const checkoutUrl = useBuildCommerceUrl(
    CommercePath.PaymentRedirect,
    courseRunSku && course.activeCourseRun?.key
      ? {
          sku: courseRunSku,
          course_run_key: course.activeCourseRun.key,
        }
      : undefined,
    "login",
    true, // byPassAuth to get the raw commerce URL
  );

  return checkoutUrl;
}
